import { TypographyOptions } from '@mui/material/styles/createTypography';

/**
 * Two-font system, mapped onto the existing variant keys.
 *  - Inter (primary, display & body): https://fonts.google.com/specimen/Inter
 *    Used for headers (header1–4), running text (text1/text2), and actions
 *    (action1/action2). Weights 400 / 500 / 600 / 700.
 *  - JetBrains Mono (technical, labels & data):
 *    https://fonts.google.com/specimen/JetBrains+Mono
 *    Used for the textAlt* channel — eyebrows, IDs, table data, units, code.
 *    "Machine-truthful" accent; never for paragraphs. Weights 400 / 500.
 *
 * Sizes/weights/spacing follow the Prolipsium visual identity guideline.
 */

const fontFamily = {
  INTER: 'Inter',
  JETBRAINS_MONO: 'JetBrains Mono',
};

// font-family: 'Inter', sans-serif;
// font-family: 'JetBrains Mono', monospace;

const typography: TypographyOptions = {
  fontFamily: [fontFamily.INTER, 'sans-serif'].join(','),

  // Running text — Inter.
  // text1: lede / large body — section intros, prominent paragraphs.
  text1: {
    fontFamily: fontFamily.INTER,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
  },
  // text2: default body copy.
  text2: {
    fontFamily: fontFamily.INTER,
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.65,
    letterSpacing: 0,
  },

  // Mono channel — JetBrains Mono. Use for IDs, table data, units, eyebrows.
  // textAlt1: primary mono — table cells, inline code, identifiers.
  textAlt1: {
    fontFamily: fontFamily.JETBRAINS_MONO,
    fontSize: 15,
    textTransform: 'uppercase',
    fontWeight: 500,
    letterSpacing: 3,
  },
  // textAlt2: eyebrow / kicker label — small uppercase tag above headings.
  textAlt2: {
    fontFamily: fontFamily.JETBRAINS_MONO,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },

  // Buttons / interactive labels — Inter.
  // action1: large CTA / primary button.
  action1: {
    fontFamily: fontFamily.INTER,
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: -0.3,
  },
  // action2: standard button label.
  action2: {
    fontFamily: fontFamily.INTER,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.2,
  },

  // Headings — Inter, descending scale.
  // header1: display section heading.
  header1: {
    fontFamily: fontFamily.INTER,
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: -0.8,
    lineHeight: 1.15,
  },
  // header2: sub-display / document-level heading.
  header2: {
    fontFamily: fontFamily.INTER,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -0.4,
  },
  // header3: standard h3.
  header3: {
    fontFamily: fontFamily.INTER,
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: -0.3,
  },
  // header4: small heading / card title.
  header4: {
    fontFamily: fontFamily.INTER,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.2,
  },

  h1: undefined,
  h2: undefined,
  h3: undefined,
  h4: undefined,
  h5: undefined,
  h6: undefined,
  subtitle1: undefined,
  subtitle2: undefined,
  body1: undefined,
  body2: undefined,
  button: undefined,
  // caption: tag / chip label — smaller than textAlt2, used inside coloured
  // pills (StatusReadout). Tight letter-spacing because the background
  // already carries the visual weight.
  caption: {
    fontFamily: fontFamily.JETBRAINS_MONO,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 1,
    textTransform: 'uppercase',
    lineHeight: 1,
  },
  overline: undefined,
};

export { fontFamily, typography };
